import { Link, useParams } from "react-router-dom";
import old1_PublicBackLink from "../components/old1_PublicBackLink";
import old1_PublicSiteNav from "../components/old1_PublicSiteNav";
import old1_PublicSiteFooter from "../components/old1_PublicSiteFooter";
import PageSeo from "../../components/PageSeo";
import LegalMarkdown from "../../components/LegalMarkdown";
import { getLegalDoc, getLegalVersion } from "../../legal/legalDocuments";
import { SITE_NAME, buildPublicPageUrl } from "../../lib/pageSeo";

const SEO_DESCRIPTIONS = {
  terms:
    "Read the JOBUWIN Terms of Use — the agreement that keeps hiring genuine, honest and human for everyone on the platform.",
  privacy:
    "Read the JOBUWIN Privacy Policy — how JOBUWIN collects, uses, stores and protects your personal information.",
};

const PUBLISHER = {
  "@type": "Organization",
  name: SITE_NAME,
  legalName: "MotoMob Limited",
  url: buildPublicPageUrl("/"),
};

function buildLegalJsonLd({ doc, resolved, description }) {
  if (!doc || !resolved) return undefined;
  const canonical = buildPublicPageUrl(`/old1${doc.path}`);
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: `${doc.label} | ${SITE_NAME}`,
      description,
      url: canonical,
      inLanguage: "en-GB",
      ...(resolved.effectiveDateIso
        ? { datePublished: resolved.effectiveDateIso, dateModified: resolved.effectiveDateIso }
        : {}),
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: buildPublicPageUrl("/old1/home") },
      publisher: PUBLISHER,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: buildPublicPageUrl("/old1/home") },
        { "@type": "ListItem", position: 2, name: doc.label, item: canonical },
      ],
    },
  ];
}

export default function old1_LegalDocumentPage({ docType, currentUser, setCurrentUser }) {
  const { version } = useParams();
  const homeHref = "/old1/home";
  const authBase = "/old1";
  const doc = getLegalDoc(docType);
  const resolved = getLegalVersion(docType, version);
  const description = SEO_DESCRIPTIONS[docType] || "JOBUWIN legal information.";
  const isCurrent = Boolean(resolved && resolved.isCurrent);
  const seoTitle = doc
    ? isCurrent || !resolved
      ? doc.label
      : `${doc.label} (v${resolved.version})`
    : "Legal";
  const pagePath =
    resolved && !isCurrent && doc
      ? `/old1${doc.path}/${resolved.version}`
      : doc
        ? `/old1${doc.path}`
        : "/old1";

  const legalArticle = resolved ? (
    <article className="jw-public-card jw-public-legal__card">
      <header className="jw-public-legal__head">
        <span className="jw-public-eyebrow">{doc.label}</span>
        <p className="jw-public-legal__meta">
          Version {resolved.version} &middot; Effective {resolved.effectiveDate}
        </p>
        {!resolved.isCurrent ? (
          <p className="jw-public-legal__archived">
            You&apos;re viewing an older version.{" "}
            <Link to={`${authBase}${doc.path}`}>Read the current {doc.label}</Link>.
          </p>
        ) : null}
      </header>
      <LegalMarkdown markdown={resolved.content} />
    </article>
  ) : (
    <article className="jw-public-card jw-public-legal__card">
      <h1>We couldn&apos;t find that document</h1>
      <p>
        That version isn&apos;t available.{" "}
        {doc ? (
          <Link to={`${authBase}${doc.path}`}>Read the current {doc.label}</Link>
        ) : (
          <Link to={homeHref}>Head back home</Link>
        )}
        .
      </p>
    </article>
  );

  return (
    <div className="home-page jw-public-page jw-public-home jw-public-legal-page">
      <PageSeo
        title={seoTitle}
        description={description}
        path={resolved && !isCurrent ? `/old1${doc.path}/${resolved.version}` : doc ? `/old1${doc.path}` : "/old1"}
        canonicalPath={doc ? `/old1${doc.path}` : "/old1"}
        type="article"
        articlePublishedTime={resolved?.effectiveDateIso}
        articleModifiedTime={resolved?.effectiveDateIso}
        noindex={!resolved || !isCurrent}
        jsonLd={isCurrent ? buildLegalJsonLd({ doc, resolved, description }) : undefined}
      />

      <old1_PublicSiteNav currentUser={currentUser} setCurrentUser={setCurrentUser} />

      <main id="main-content" className="jw-public-main">
        <section className="jw-public-section jw-public-legal">
          <old1_PublicBackLink fallbackTo="/old1/home" fixed />
          {legalArticle}
        </section>
      </main>

      <old1_PublicSiteFooter />
    </div>
  );
}
